import { useTranslation } from 'react-i18next'
import { MenuItem, Stack, TextField, Typography } from '@mui/material'

import { ConnectorDeviceProfile, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'

import { DeviceSettingsField, getDeviceSettingsSpec } from '../../model/device-settings-spec'
import { useBluetoothScan } from '../../model/useBluetoothScan'
import { useSerialPorts } from '../../model/useSerialPorts'
import BluetoothUnitPicker from './BluetoothUnitPicker'
import SerialPortPicker from './SerialPortPicker'

export type ConnectDeviceStepProps = {
  draft: MedconnectDeviceValue
  profile: ConnectorDeviceProfile | undefined
  onChange: (patch: Partial<MedconnectDeviceValue>) => void
}

/** Third step: find the unit (Bluetooth scan / serial port) and fill the few settings the device needs. */
export default function ConnectDeviceStep({ draft, profile, onChange }: ConnectDeviceStepProps) {
  const { t } = useTranslation()
  const spec = getDeviceSettingsSpec(draft.name)
  const scan = useBluetoothScan(profile?.advertisedNamePatterns ?? [], spec.connection === 'bluetooth')
  const ports = useSerialPorts(spec.connection === 'serial')

  return (
    <Stack spacing={3}>
      {spec.connection === 'bluetooth' && (
        <BluetoothUnitPicker scan={scan} selectedId={draft.ble_id} onSelect={(id) => onChange({ ble_id: id })} />
      )}
      {spec.connection === 'serial' && (
        <SerialPortPicker ports={ports} value={draft.port} onChange={(port) => onChange({ port })} />
      )}
      {spec.fields.length > 0 && (
        <Stack spacing={2}>
          <Typography variant="subtitle2">{t(`page.configuration.MedconnectDevice.wizard.connection.settingsTitle.${spec.connection}`)}</Typography>
          {spec.fields.map((field) => (
            <SettingsField key={field.name} field={field} value={draft[field.name]} onChange={(value) => onChange({ [field.name]: value })} />
          ))}
        </Stack>
      )}
    </Stack>
  )
}

type SettingsFieldProps = {
  field: DeviceSettingsField
  value: MedconnectDeviceValue[DeviceSettingsField['name']]
  onChange: (value: string) => void
}

function SettingsField({ field, value, onChange }: SettingsFieldProps) {
  const { t } = useTranslation()
  const label = t(`page.configuration.MedconnectDevice.wizard.fields.${field.labelKey}.label`)
  const hint = t(`page.configuration.MedconnectDevice.wizard.fields.${field.labelKey}.hint`, { defaultValue: '' })
  const stringValue = value === undefined || value === null ? '' : String(value)

  return (
    <TextField
      select={!!field.options}
      fullWidth
      size="small"
      required={field.required}
      label={label}
      helperText={hint}
      value={stringValue}
      onChange={(event) => onChange(event.target.value)}
    >
      {field.options?.map((option) => (
        <MenuItem key={option} value={option}>{option}</MenuItem>
      ))}
    </TextField>
  )
}
